import { Badge, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, GridList, GridListTile, IconButton, InputAdornment, InputLabel, List, ListItem, ListItemIcon, ListItemText, makeStyles, MenuItem, Select, Tab, Tabs, TextField, Theme, useTheme } from "@material-ui/core";
import { green, orange, red, yellow } from "@material-ui/core/colors";
import AddPhotoIcon from '@material-ui/icons/AddAPhotoOutlined';
import SizeIcon from '@material-ui/icons/AspectRatio';
import DeviationsIcon from '@material-ui/icons/AssignmentOutlined';
import PhotosIcon from '@material-ui/icons/CameraAltOutlined';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/ErrorOutlined';
import LabelIcon from '@material-ui/icons/Label';
import PrintOutlinedIcon from '@material-ui/icons/PrintOutlined';
import WarningIcon from '@material-ui/icons/ReportProblemOutlined';
import SaveOutlinedIcon from '@material-ui/icons/SaveOutlined';
import WeightIcon from '@material-ui/icons/Speed';
import QuantityIcon from '@material-ui/icons/WidgetsOutlined';
import * as ImageConversion from 'image-conversion';
import React, { useEffect, useState } from "react";
import { useRouteMatch } from "react-router-dom";
import SwipeableViews from "react-swipeable-views";
import { DangerousGoodsItem, DeviationType, GetReceiptItem, Unit, usePrintLabelMutation } from "../../graphql/generated/graphql";
import AddPhotoDialog from "./AddPhotoDialog";
import DangerousGoodItem from "./DangerousGoodsItem";
import PrintLabelsResultDialog from "./PrintLabelsResultDialog";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    paddingLeft: theme.spacing(1),
  },
  scaled: {
    display: "block",
    marginLeft: "auto",
    marginRight: "auto",
    maxWidth: 250,
    marginTop: theme.spacing(2),
    "& input": {
      fontSize: 36,
    },
    "& .MuiSelect-root": {
      maxWidth: 186,
    },
  },
  startAdornment: {
    "& p": {
      fontSize: 28,
      paddingTop: 7
    },
  },
  endAdornment: {
    "& p": {
      fontSize: 28,
      paddingTop: 25,
    }
  },
  swipableViews: {
    height: "calc(100vh - 189px)",
    "& > *": {
      height: "100%",
    },
  },
  errorBadge: {
    "& .MuiBadge-badge": {
      backgroundColor: red[600],
      color: theme.palette.getContrastText(red[600]),
    },
  },
  warningBadge: {
    "& .MuiBadge-badge": {
      backgroundColor: yellow[600],
      color: theme.palette.getContrastText(yellow[600]),
    },
  },
  deviationIcon: {
    minWidth: 36,
  },
  deviationText: {
    "& .MuiListItemText-primary": {
      fontSize: 18,
    }
  },
  dialogContent: {
    padding: 0
  },
  tabs: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  addPhotoIcon: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)"
  },
  photosPanel: {
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    paddingTop: theme.spacing(0.5),
  },
  centerBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  iconError: {
    marginRight: theme.spacing(1),
    float: "left",
    color: red[800],
    marginTop: "-0.375rem",
  },
  iconGreen: {
    color: green[500],
    fontSize: 100,
  },
  iconRed: {
    color: red[500],
    fontSize: 100,
  },
}));

function a11yProps(index: any)
{
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`,
  };
}

interface TabPanelProps
{
  children?: React.ReactNode;
  dir?: string;
  index: any;
  value: any;
  nextValue: any;
  className?: string;
}

const TabPanel = (props: TabPanelProps) =>
{
  const { children, value, index, nextValue, ...other } = props;

  const visible = value === index || nextValue === index;

  return (
    <div
      role="tabpanel"
      hidden={!visible}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {visible ? children : null}
    </div>
  );
}

interface Inputs
{
  Quantity: string;
  UnitId: number;
  PalletSpaces: string;
  Weight: string;
  Length: string;
  Width: string;
  Height: string;
  CheckedDeviationTypeIds: number[];
  Photos: string[];
  Thumbnails: string[];
}

interface Validation
{
  QuantityError: boolean;
  PalletSpacesError: boolean;
  QuantityErrors: number;
  QuantityWarnings: number;
  WeightError: boolean;
  WeightErrors: number;
  WeightWarnings: number;
  LengthError: boolean;
  WidthError: boolean;
  HeightError: boolean;
  DimensionErrors: number;
  DimensionWarnings: number;
  DeviationWarnings: number;
  PhotoErrors: number;
  PhotoWarnings: number;
}

const TabIcon = (props: { errors: number, warnings: number, children: React.ReactNode }) =>
{
  const classes = useStyles();
  if(props.errors > 0) return <Badge badgeContent={props.errors} className={classes.errorBadge}>{props.children}</Badge>;
  if(props.warnings > 0) return <Badge badgeContent={props.warnings} className={classes.warningBadge}>{props.children}</Badge>;
  return <>{props.children}</>;
}

export default function Component(props: {
  Item: GetReceiptItem,
  DeviationTypes: DeviationType[],
  Units: Unit[],
  DangerousGoods: DangerousGoodsItem[] | null,
  DangerousGoodsFeature: boolean,
  onClose: (Item: GetReceiptItem, ShouldRefetch: boolean) => void,
})
{
  const classes = useStyles();
  const theme = useTheme();
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [nextTabIndex, setNextTabIndex] = useState(0);
  const [addPhotoIsOpen, setAddPhotoIsOpen] = useState(false);
  const match = useRouteMatch<{ receiptId: string }>("/reception/:receiptId");

  const handleTabChange = (event: React.ChangeEvent<{}>, index: number) =>
  {
    setNextTabIndex(currentTabIndex);
    setCurrentTabIndex(index);
  };

  const handleSwipeChange = (index: number) =>
  {
    setNextTabIndex(currentTabIndex);
    setCurrentTabIndex(index);
  };

  const onSwitching = (index: number) =>
  {
    if(index < currentTabIndex && nextTabIndex === currentTabIndex)
      setNextTabIndex(currentTabIndex - 1);
    if(index > currentTabIndex && nextTabIndex === currentTabIndex)
      setNextTabIndex(currentTabIndex + 1);
    if(index === currentTabIndex && nextTabIndex !== currentTabIndex)
      setNextTabIndex(currentTabIndex);
  };

  const handleTransitionEnd = () =>
  {
    setNextTabIndex(currentTabIndex);
  }

  const [inputs, setInputs] = useState<Inputs>({
    Quantity: `${props.Item?.FactQuantity ?? props.Item?.Quantity}`,
    UnitId: props.Item?.FactQuantityUnit?.UnitId ?? props.Item?.QuantityUnit?.UnitId,
    PalletSpaces: `${props.Item?.FactPalletSpaces ?? props.Item?.IndicatedPalletSpaces}`,
    Weight: `${props.Item?.FactWeight ?? props.Item?.IndicatedWeight}`,
    Length: `${props.Item?.FactLength ?? props.Item?.IndicatedLength}`,
    Width: `${props.Item?.FactWidth ?? props.Item?.IndicatedWidth}`,
    Height: `${props.Item?.FactHeight ?? props.Item?.IndicatedHeight}`,
    CheckedDeviationTypeIds: props.Item ? [...props.Item?.Deviations!] : [],
    Photos: props.Item ? [...props.Item?.Photos!] : [],
    Thumbnails: [],
  });

  useEffect(() =>
  {
    (async (photos) =>
    {
      const thumbnails: string[] = [];
      for(const photo of photos)
      {
        const regex = /data:(?<mediatype>.*?)(;(?<encoding>.*))?,(?<data>.*)/gm;
        const matches = regex.exec(photo);
        if(matches)
        {
          const blob = await ImageConversion.dataURLtoFile(photo);
          const thumbnailBlob = await ImageConversion.compressAccurately(blob, { height: 160, accuracy: 0.6, size: 10, });
          const thumbnail = await ImageConversion.filetoDataURL(thumbnailBlob);
          thumbnails.push(thumbnail);
        }
        else
        {
          thumbnails.push(photo);
        }
      }
      const newInputs = { ...inputs, Thumbnails: thumbnails };
      setInputs(newInputs);
    })(props.Item.Photos);
    // eslint-disable-next-line
  }, [props.Item.Photos]);

  function validate(i: number | null | undefined, f: string | number | null | undefined, checkedDeviationTypeIds?: number[])
  {
    if(typeof (f) === "string") f = parseInt(f);

    const skipError = checkedDeviationTypeIds?.includes(3 /* Hiány */);

    if(!((f ?? 0) > 0)) return { errors: skipError ? 0 : 1, error: !skipError, warnings: 0 };
    // warns if the indicated value is different from the fact value
    // but only if indicated value has been set
    if(i && i !== f) return { errors: 0, error: false, warnings: 1 };
    return { errors: 0, error: false, warnings: 0 }
  }

  function validatePalletSpaces(i: number | null | undefined, max: string | number | null | undefined, f: string | number | null | undefined)
  {
    if(typeof (f) === "string") f = parseInt(f);
    if(typeof (max) === "string") max = parseInt(max);
    if(f == null || max == null || !(f >= 0 && f <= max)) return { errors: 1, error: true, warnings: 0 };
    // warns if the indicated value is different from the fact value
    // but only if indicated value has been set
    if(i != null && i !== f) return { errors: 0, error: false, warnings: 1 };
    return { errors: 0, error: false, warnings: 0 }
  }

  function isInputFieldDisabled(checkedDeviationTypeIds: number[]): boolean
  {
    return checkedDeviationTypeIds.includes(3); /* Hiány */
  }

  const photoRequiredDeviationTypeIds = props?.DeviationTypes.filter(dt => dt.Photo).map(dt => dt.DeviationTypeId);

  const getValidation = (defaults: boolean = false, inp: Inputs = inputs): Validation =>
  {
    const quantity = validate(props.Item?.Quantity, defaults ? props.Item?.FactQuantity : inp.Quantity, inp.CheckedDeviationTypeIds);
    const palletSpaces = validatePalletSpaces(props.Item?.IndicatedPalletSpaces, defaults ? props.Item?.FactQuantity : inp.Quantity, defaults ? props.Item?.FactPalletSpaces : inp.PalletSpaces);
    const weight = validate(props.Item?.IndicatedWeight, defaults ? props.Item?.FactWeight : inp.Weight, inp.CheckedDeviationTypeIds);
    const length = validate(props.Item?.IndicatedLength, defaults ? props.Item?.FactLength : inp.Length, inp.CheckedDeviationTypeIds);
    const width = validate(props.Item?.IndicatedWidth, defaults ? props.Item?.FactWidth : inp.Width, inp.CheckedDeviationTypeIds);
    const height = validate(props.Item?.IndicatedHeight, defaults ? props.Item?.FactHeight : inp.Height, inp.CheckedDeviationTypeIds);
    return {
      QuantityError: quantity.error || !inp.UnitId,
      PalletSpacesError: palletSpaces.error,
      QuantityErrors: quantity.errors + (inp.UnitId ? 0 : 1) + palletSpaces.errors,
      QuantityWarnings: quantity.warnings + (inp.UnitId !== props.Item?.QuantityUnit?.UnitId ? 1 : 0) + palletSpaces.warnings,
      WeightErrors: weight.errors,
      WeightError: weight.error,
      WeightWarnings: weight.warnings,
      LengthError: length.error,
      WidthError: width.error,
      HeightError: height.error,
      DimensionErrors: length.errors + width.errors + height.errors,
      DimensionWarnings: length.warnings + width.warnings + height.warnings,
      DeviationWarnings: inp.CheckedDeviationTypeIds.length,
      PhotoErrors: inp.CheckedDeviationTypeIds.some(id => photoRequiredDeviationTypeIds.includes(id)) && inp.Photos.length === 0 ? 1 : 0,
      PhotoWarnings: inp.Photos.length,
    };
  }

  const [validation, setValidation] = useState<Validation>(getValidation(true));

  const handleInputChange = (prop: keyof Inputs) => (event: React.ChangeEvent<HTMLInputElement>) =>
  {
    const newInputs = { ...inputs, [prop]: event.target.value };
    const newValidation = getValidation(false, newInputs);

    const weightDeviationIndex = newInputs.CheckedDeviationTypeIds.indexOf(1);
    if(newValidation.WeightWarnings > 0 && weightDeviationIndex === -1)
    {
      newInputs.CheckedDeviationTypeIds.push(1);
    }
    else
    {
      if(newValidation.WeightWarnings === 0 && weightDeviationIndex !== -1)
      {
        newInputs.CheckedDeviationTypeIds.splice(weightDeviationIndex, 1);
      }
    }

    const dimensionDeviationIndex = newInputs.CheckedDeviationTypeIds.indexOf(2);
    if(newValidation.DimensionWarnings > 0 && dimensionDeviationIndex === -1)
    {
      newInputs.CheckedDeviationTypeIds.push(2);
    }
    else
    {
      if(newValidation.DimensionWarnings === 0 && dimensionDeviationIndex !== -1)
      {
        newInputs.CheckedDeviationTypeIds.splice(dimensionDeviationIndex, 1);
      }
    }

    setInputs(newInputs);
  };

  const handleSelectChange = (prop: keyof Inputs) => (event: React.ChangeEvent<{ value: unknown }>) =>
  {
    const newInputs = { ...inputs, [prop]: event.target.value };
    setInputs(newInputs);
  };

  const addPhoto = (picture: Blob) =>
  {
    setAddPhotoIsOpen(false);
    (async (blob) =>
    {
      let thumbnailBlob: Blob | null = null;
      if(blob.type === 'image/png')
      {
        const pngDataUrl = await ImageConversion.filetoDataURL(blob);
        const jpegFile = await ImageConversion.dataURLtoFile(pngDataUrl, ImageConversion.EImageType.JPEG);
        blob = await ImageConversion.compress(jpegFile, 0.8);
        thumbnailBlob = await ImageConversion.compressAccurately(jpegFile, { height: 160, accuracy: 0.6, size: 10, });
      }
      if(!thumbnailBlob) thumbnailBlob = await ImageConversion.compressAccurately(blob, { height: 160, accuracy: 0.6, size: 10, });
      const photo = await ImageConversion.filetoDataURL(blob);
      const thumbnail = await ImageConversion.filetoDataURL(thumbnailBlob);
      const newInputs = { ...inputs, Photos: [...inputs.Photos, photo], Thumbnails: [...inputs.Thumbnails, thumbnail] };
      setInputs(newInputs);
    })(picture);
  };

  useEffect(() =>
  {
    setValidation(getValidation());
    // eslint-disable-next-line
  }, [inputs]);

  const toggleDeviationTypeId = (DeviationTypeId: number) => () =>
  {
    if(DeviationTypeId <= 2) return;
    const currentIndex = inputs.CheckedDeviationTypeIds.indexOf(DeviationTypeId);
    const newChecked = [...inputs.CheckedDeviationTypeIds];

    if(currentIndex === -1)
    {
      newChecked.push(DeviationTypeId);

      if(DeviationTypeId === 3 /* Hiány */)
      {
        inputs.PalletSpaces = '0';
        inputs.Weight = '0';
        inputs.Quantity = '0';
        inputs.Height = '0';
        inputs.Length = '0';
        inputs.Width = '0';
      }
    }
    else
    {
      newChecked.splice(currentIndex, 1);
    }
    setInputs({ ...inputs, CheckedDeviationTypeIds: newChecked });
  };

  const [printTriggered, setPrintTriggered] = useState(false);

  const [printLabel] = usePrintLabelMutation();
  const [printLabelResult, setPrintLabelResult] = useState<boolean | null>(null);

  const handlePrint = async () =>
  {
    const printLabelsResult = (await printLabel({ variables: { ReceiptId: Number.parseInt(match?.params.receiptId ?? ""), ReceiptActivityId: props?.Item?.ReceiptActivityId } })).data!;
    setPrintLabelResult(printLabelsResult.PrintLabel);
    setPrintTriggered(true);
  }

  const handleClose = () =>
  {
    const item = { ...props.Item! };
    item.FactQuantity = parseInt(inputs.Quantity);
    item.FactQuantityUnit = props.Units.find(u => u.UnitId === inputs.UnitId);
    item.FactPalletSpaces = parseInt(inputs.PalletSpaces);
    item.FactWeight = parseInt(inputs.Weight);
    item.FactLength = parseInt(inputs.Length);
    item.FactWidth = parseInt(inputs.Width);
    item.FactHeight = parseInt(inputs.Height);
    item.Deviations = inputs.CheckedDeviationTypeIds;
    item.Photos = inputs.Photos;
    props.onClose(item, printTriggered);
  }

  const [isDecidedDangerousGoods, setIsDecidedDangerousGoods] = useState(props.DangerousGoods == null || props.DangerousGoods.length === 0);

  const handleDangerousGoodsAccept = (isChecked: boolean) =>
  {
    setIsDecidedDangerousGoods(isChecked);

    const newInputs = { ...inputs };
    const dangerousGoodsDeviation = newInputs.CheckedDeviationTypeIds.indexOf(24);

    if(dangerousGoodsDeviation !== -1)
    {
      newInputs.CheckedDeviationTypeIds.splice(dangerousGoodsDeviation, 1);
    }

    setInputs(newInputs);
  };


  const handleDangerousGoodsReject = (isChecked: boolean) =>
  {
    setIsDecidedDangerousGoods(isChecked);

    const newInputs = { ...inputs };
    const dangerousGoodsDeviation = newInputs.CheckedDeviationTypeIds.indexOf(24);

    if(dangerousGoodsDeviation === -1)
    {
      newInputs.CheckedDeviationTypeIds.push(24);
    }

    setInputs(newInputs);
  }

  return (
    <Dialog fullScreen open={props.Item != null}>
      <DialogTitle>
        {props.DangerousGoodsFeature && props.DangerousGoods && props.DangerousGoods.length > 0 && (<WarningIcon style={{ marginRight: 8, verticalAlign: 'text-top', color: `${orange[500]}` }} />)}
        {props.Item?.PackageNumber}
        {props.DangerousGoodsFeature && props.DangerousGoods && props.DangerousGoods.length > 0 && (<WarningIcon style={{ marginLeft: 8, verticalAlign: 'text-top', color: `${orange[500]}` }} />)}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Tabs className={classes.tabs} variant="scrollable" value={currentTabIndex} onChange={handleTabChange} scrollButtons="auto">
          <Tab label="Menny." icon={<TabIcon errors={validation.QuantityErrors} warnings={validation.QuantityWarnings}> <QuantityIcon /></TabIcon>} {...a11yProps(0)} />
          <Tab label="Súly" icon={<TabIcon errors={validation.WeightErrors} warnings={validation.WeightWarnings}> <WeightIcon /></TabIcon>} {...a11yProps(1)} />
          <Tab label="Méret" icon={<TabIcon errors={validation.DimensionErrors} warnings={validation.DimensionWarnings}><SizeIcon /></TabIcon>} {...a11yProps(2)} />
          <Tab label="Jegyzők." icon={<TabIcon errors={0} warnings={validation.DeviationWarnings}> <DeviationsIcon /></TabIcon>} {...a11yProps(3)} />
          <Tab label="Fotók" icon={<TabIcon errors={validation.PhotoErrors} warnings={validation.PhotoWarnings}> <PhotosIcon /></TabIcon>} {...a11yProps(4)} />
          <Tab label="Címke" icon={<TabIcon errors={0} warnings={0}> <LabelIcon /></TabIcon>} {...a11yProps(5)} />
          {props.DangerousGoodsFeature && <Tab label="ADR" icon={<TabIcon errors={isDecidedDangerousGoods ? 0 : 1} warnings={0}> <WarningIcon /></TabIcon>} {...a11yProps(6)} />}
        </Tabs>
        <SwipeableViews id="sw" className={classes.swipableViews} index={currentTabIndex} onChangeIndex={handleSwipeChange} onSwitching={onSwitching} onTransitionEnd={handleTransitionEnd}>
          <TabPanel value={currentTabIndex} nextValue={nextTabIndex} index={0} dir={theme.direction}>
            <TextField value={inputs.Quantity} onChange={handleInputChange('Quantity')} className={classes.scaled} type="number" label="Mennyiség" variant="filled"
              InputProps={{
                error: validation.QuantityError,
                startAdornment: <InputAdornment position="start" className={classes.startAdornment}><p>{props.Item?.Quantity ?? '-'} / </p></InputAdornment>,
              }}
              disabled={isInputFieldDisabled(inputs.CheckedDeviationTypeIds)}
            />
            <FormControl variant="filled" className={classes.scaled}>
              <InputLabel id="unit-label">Mennyiségi egység</InputLabel>
              <Select labelId="unit-label" value={inputs.UnitId} className={classes.scaled} onChange={handleSelectChange('UnitId')}>
                {props.Units.map((unit) => <MenuItem key={unit.UnitId} value={unit.UnitId} selected={inputs.UnitId === unit.UnitId}>{`${props.Item.QuantityUnit.UnitId === unit.UnitId ? '✓ ' : ''}${unit.Name}`}</MenuItem >)}
              </Select>
            </FormControl>
            <TextField value={inputs.PalletSpaces} onChange={handleInputChange('PalletSpaces')} className={classes.scaled} type="number" label="Raklaphely" variant="filled"
              InputProps={{
                error: validation.PalletSpacesError,
                startAdornment: <InputAdornment position="start" className={classes.startAdornment}><p>{props.Item?.IndicatedPalletSpaces ?? '-'} / </p></InputAdornment>,
                endAdornment: <InputAdornment position="end" className={classes.endAdornment}>db</InputAdornment>
              }}
              disabled={isInputFieldDisabled(inputs.CheckedDeviationTypeIds)}
            />
          </TabPanel>
          <TabPanel value={currentTabIndex} nextValue={nextTabIndex} index={1} dir={theme.direction}>
            <TextField value={inputs.Weight} onChange={handleInputChange('Weight')} className={classes.scaled} type="number" label="Súly" variant="filled"
              InputProps={{
                error: validation.WeightError,
                startAdornment: <InputAdornment position="start" className={classes.startAdornment}><p>{props.Item?.IndicatedWeight ?? '-'} / </p></InputAdornment>,
                endAdornment: <InputAdornment position="end" className={classes.endAdornment}>kg</InputAdornment>
              }}
              disabled={isInputFieldDisabled(inputs.CheckedDeviationTypeIds)}
            />
          </TabPanel>
          <TabPanel value={currentTabIndex} nextValue={nextTabIndex} index={2} dir={theme.direction}>
            <TextField value={inputs.Width} onChange={handleInputChange('Width')} className={classes.scaled} type="number" label="Szélesség" defaultValue={props.Item?.FactWidth ?? props.Item?.IndicatedWidth} variant="filled"
              InputProps={{
                error: validation.WidthError,
                startAdornment: <InputAdornment position="start" className={classes.startAdornment}><p>{props.Item?.IndicatedWidth ?? '-'} / </p></InputAdornment>,
                endAdornment: <InputAdornment position="end" className={classes.endAdornment}>cm</InputAdornment>
              }}
              disabled={isInputFieldDisabled(inputs.CheckedDeviationTypeIds)}
            />
            <TextField value={inputs.Length} onChange={handleInputChange('Length')} className={classes.scaled} type="number" label="Hossz" defaultValue={props.Item?.FactLength ?? props.Item?.IndicatedLength} variant="filled"
              InputProps={{
                error: validation.LengthError,
                startAdornment: <InputAdornment position="start" className={classes.startAdornment}><p>{props.Item?.IndicatedLength ?? '-'} / </p></InputAdornment>,
                endAdornment: <InputAdornment position="end" className={classes.endAdornment}>cm</InputAdornment>
              }}
              disabled={isInputFieldDisabled(inputs.CheckedDeviationTypeIds)}
            />
            <TextField value={inputs.Height} onChange={handleInputChange('Height')} className={classes.scaled} type="number" label="Magasság" defaultValue={props.Item?.FactHeight ?? props.Item?.IndicatedHeight} variant="filled"
              InputProps={{
                error: validation.HeightError,
                startAdornment: <InputAdornment position="start" className={classes.startAdornment}><p>{props.Item?.IndicatedHeight ?? '-'} / </p></InputAdornment>,
                endAdornment: <InputAdornment position="end" className={classes.endAdornment}>cm</InputAdornment>
              }}
              disabled={isInputFieldDisabled(inputs.CheckedDeviationTypeIds)}
            />
          </TabPanel>
          <TabPanel value={currentTabIndex} nextValue={nextTabIndex} index={3} dir={theme.direction}>
            <List>
              {props.DeviationTypes.map(dt => <ListItem disabled={dt.DeviationTypeId <= 2} key={dt.DeviationTypeId} dense onClick={toggleDeviationTypeId(dt.DeviationTypeId)}>
                <ListItemIcon className={classes.deviationIcon}>
                  <Checkbox checked={inputs.CheckedDeviationTypeIds.indexOf(dt.DeviationTypeId) !== -1} disableRipple />
                </ListItemIcon>
                <ListItemText className={classes.deviationText} id={`checkbox-list-label-${dt.DeviationTypeId}`} primary={dt.Name} />
              </ListItem>)}
            </List>
          </TabPanel>
          <TabPanel value={currentTabIndex} nextValue={nextTabIndex} index={4} dir={theme.direction} className={classes.photosPanel}>
            <GridList cellHeight={160} cols={3}>
              {inputs.Thumbnails.map((p, i) => <GridListTile key={i} >
                <img src={p} decoding="async" loading="lazy" alt="Fotó" />
              </GridListTile>)}
              <GridListTile key={0}>
                <IconButton className={classes.addPhotoIcon} onClick={() => setAddPhotoIsOpen(true)}>
                  <AddPhotoIcon fontSize="large" />
                </IconButton>
              </GridListTile>
            </GridList>
          </TabPanel>
          <TabPanel value={currentTabIndex} nextValue={nextTabIndex} index={5} dir={theme.direction}>
            {props.Item.SingleLabel ? (
              <Box className={classes.centerBox}>
                <ErrorIcon fontSize="large" className={classes.iconError} />
                Egy címkés esetén nem lehet +1 címkét nyomtatni
              </Box>
            ) : null}
            <Box className={classes.centerBox}>
              <Button variant="contained" color="primary" startIcon={<PrintOutlinedIcon />} onClick={handlePrint} disabled={props.Item.SingleLabel}>+1 Címke</Button>
              {printLabelResult == null ? null : <PrintLabelsResultDialog onClose={() => setPrintLabelResult(null)} isSuccess={printLabelResult!} />}
            </Box>
          </TabPanel>
          <TabPanel value={currentTabIndex} nextValue={nextTabIndex} index={6} dir={theme.direction}>
            <Grid container className={classes.root}>
              {props?.DangerousGoods?.map((i, index) => <DangerousGoodItem
                key={i.OrderItemDangerousGoodsId}
                Item={i}
              />)}
              {props?.DangerousGoods?.length === 0 && <Box className={classes.centerBox} fontSize="x-large">Veszélyes árura vonatkozó információ nem szerepel a rendszerben.</Box>}
            </Grid>
            <Box className={classes.centerBox}>
              <IconButton onClick={() => handleDangerousGoodsAccept(true)}>
                <CheckCircleIcon className={classes.iconGreen} />
              </IconButton>
              <IconButton onClick={() => handleDangerousGoodsReject(true)}>
                <CancelIcon className={classes.iconRed} />
              </IconButton>
            </Box>
          </TabPanel>
        </SwipeableViews>
        <AddPhotoDialog dialogOpen={addPhotoIsOpen} onClose={(photo) =>
        {
          addPhoto(photo);
        }} />
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" disabled={validation.WeightErrors > 0 || validation.DimensionErrors > 0 || validation.PhotoErrors > 0 || (props.DangerousGoodsFeature && !isDecidedDangerousGoods)} startIcon={<SaveOutlinedIcon />} onClick={handleClose}>Átvéve</Button>
      </DialogActions>
    </Dialog>
  );
}
